import React, { useRef, FC, useEffect, useContext, useState, useCallback } from 'react'

import Auth from 'store/auth'
import statisticContext from 'context/statisticsContext'
import WebinarSocketContext from 'context/webinarSocketContext'
import useWebinarSocket from 'hooks/useWebinarSocket'
import useThrottle from 'hooks/useThrottle'
import useSurvey from 'store/common/useSurvey'

import CheckupParameters from '../webinar-active/CheckupParameters'
import { InitRating, BeforeLoadRaiting } from '../webinar-active/star-rating/constants'
import { SessionStorageKeys } from 'constants/storage'
import { IChatMessage, IMaterial } from 'constants/types/webinar.types'
import { IObj } from 'constants/types/common.types'
import { CHAT_MESSAGES_AMOUNT, InitMsg, initCheckup, InitMsgStatus } from './data'
import { SocketEvents } from 'constants/events'

interface IWebinarSocketWrap {
  webinarData: IMaterial
  setWebinar: any
  subscribeAll: any
  children: React.ReactNode
}

const WebinarSocketWrap: FC<IWebinarSocketWrap> = ({
  webinarData,
  setWebinar,
  subscribeAll,
  children,
}) => {
  const webinarId = webinarData.id
  const statisticSocket = useContext(statisticContext)
  const webinarSocket = useWebinarSocket(webinarId)
  const { userProfile } = Auth.useAuth()

  //TODO useReducer
  const [checkup, setCheckup] = useState(initCheckup)
  const [chatMessages, setChatMessages] = useState<IChatMessage[]>([])
  const [viewers, setViewers] = useState(0)
  const [isBlocked, setBlocked] = useState(false)
  const [isFirst, setFirst] = useState(false)
  const [rating, setRating] = useState(BeforeLoadRaiting)
  const { setSurvey } = useSurvey()

  const messagesCount = useRef(0)
  const initMsgStatus = useRef(InitMsgStatus.INIT)
  const isGetMessageSend = useRef(false)

  const { firstName, secondName, id: userId } = userProfile
  const webinarStatus = webinarData.StatusId

  const sendConfirm = useCallback(() => {
    if (webinarSocket.sendEvent) {
      webinarSocket.sendEvent(SocketEvents.CHECKUP, { checkup: checkup.checkupId })
    }
    statisticSocket.sendEvent(SocketEvents.CHECKUP_SUBMIT)
  }, [webinarSocket, checkup.checkupId, userId, webinarId, statisticSocket])

  const getMessages = useCallback(
    (offset, cb) => {
      if (webinarSocket.setInQueue && !isGetMessageSend.current) {
        isGetMessageSend.current = true
        webinarSocket?.setInQueue(
          SocketEvents.GET_MESSAGES,
          {
            offset,
            amount: CHAT_MESSAGES_AMOUNT,
          },
          (_: any, message: any) => {
            const messages = message.messages.reverse()

            isGetMessageSend.current = false
            if (messages.length) {
              cb(messages)
            }
          }
        )
      }
    },
    [webinarSocket]
  )

  const getPrevMessages = useCallback(() => {
    getMessages(messagesCount.current, (messages: IChatMessage[]) => {
      setChatMessages((currentMessages) => {
        let count = currentMessages.length + messages.length
        const isInitMessage = initMsgStatus.current === InitMsgStatus.SET
        if (isInitMessage) count -= 1

        messagesCount.current = count
        return [...messages, ...currentMessages]
      })
    })
  }, [getMessages])

  const resetMessages = useCallback(() => {
    getMessages(0, (messages: IChatMessage[]) => {
      const count = messages.length
      let msg = messages
      let status = InitMsgStatus.UNSET
      const isAlreadyGreeted = sessionStorage.getItem(`greeting_${webinarId}`)

      const isInitMsgNotSet = initMsgStatus.current === InitMsgStatus.INIT && !isAlreadyGreeted
      if (isInitMsgNotSet && firstName && secondName) {
        const welcomeMsg = InitMsg(firstName, secondName)
        msg = [...messages, welcomeMsg]
        sessionStorage.setItem(`greeting_${webinarId}`, 'true')
        status = InitMsgStatus.SET
      }

      initMsgStatus.current = status
      messagesCount.current = count
      setChatMessages(msg)
    })
  }, [getMessages])

  const handleWebSocketSend = useCallback(
    (msg: string, id?: string) => {
      if (webinarSocket.sendEvent) {
        const message = id ? { msg, replyTo: id, date: new Date() } : { msg, date: new Date() }
        webinarSocket?.sendEvent(SocketEvents.CHAT_MESSAGE, message)
      }
    },
    [webinarSocket]
  )

  const subscribeAllLectors = useCallback(() => {
    if (webinarSocket.sendEvent) {
      webinarSocket?.sendEvent(SocketEvents.SUBSCRIBE_ALL_LECTORS)
      subscribeAll()
    }
  }, [webinarSocket, subscribeAll])

  const sendRating = useCallback(
    (currRating) => {
      if (webinarSocket.sendEvent) {
        webinarSocket?.sendEvent(SocketEvents.RATING, currRating)
      }
    },
    [webinarSocket]
  )

  const deleteSingleMessage = useCallback(
    (_id) => {
      if (webinarSocket.sendEvent) {
        webinarSocket?.sendEvent(SocketEvents.DELETE_SINGLE_MESSAGE, { _id })
      }
    },
    [webinarSocket]
  )
  const deleteAllUserMessages = useCallback(
    (_id) => {
      if (webinarSocket.sendEvent) {
        webinarSocket?.sendEvent(SocketEvents.DELETE_ALL_USER_MESSAGES, { _id })
      }
    },
    [webinarSocket]
  )

  const sendSurveyVote = useCallback(
    (msg: { isClosed: boolean; variants: IObj<boolean, number> }) => {
      if (webinarSocket.sendEvent) {
        const finalMessage = {
          variants: {},
        }
        webinarSocket?.sendEvent(SocketEvents.SEND_VOTES, { ...finalMessage, ...msg })
      }
    },
    [webinarSocket]
  )

  const sendFirstWatching = useCallback(() => {
    if (webinarSocket.sendEvent) {
      webinarSocket?.sendEvent(SocketEvents.WEBINAR_ONBOARDING_CLOSE)
      setFirst(true)
      sessionStorage.setItem(
        SessionStorageKeys.ONBOARDING_START_ACTIVE_WEBINAR_SCENARIO,
        JSON.stringify(true)
      )
    }
  }, [webinarSocket])

  const handleTopThrottled = useCallback(useThrottle(1000, false, getPrevMessages), [
    getPrevMessages,
  ])

  useEffect(() => {
    if (webinarSocket && webinarSocket.on) {
      messagesCount.current = 0
      isGetMessageSend.current = false

      setCheckup(initCheckup)
      setViewers(0)

      webinarSocket.on(SocketEvents.WEBINAR_STATE, (message: any) => {
        const {
          status,
          checkup: checkupId,
          successCheckup,
          blocked,
          online,
          rating: prevRating,
        } = message
        setCheckup({ checkupId, successCheckup: Boolean(successCheckup) })
        setWebinar((currentWebinar: any) => ({ ...currentWebinar, StatusId: status }))
        setBlocked(blocked)
        setViewers(online)
        setRating(prevRating || InitRating)

        if (checkupId) {
          setTimeout(() => {
            statisticSocket?.sendEvent(SocketEvents.CHECKUP_SHOW, {
              ...CheckupParameters.getAll(),
            })
          }, 3000)
        }
      })

      webinarSocket.on(SocketEvents.CHAT_MESSAGE, (message: IChatMessage) => {
        setChatMessages((currentMessages) => {
          messagesCount.current = currentMessages.length + 1
          return [...currentMessages, message]
        })
      })

      webinarSocket.on(SocketEvents.UPDATE_MESSAGE, (message: IChatMessage) => {
        setChatMessages((currentMessages) => {
          const copyMessages = [...currentMessages]
          const prevIndex = copyMessages.findIndex(({ _id }) => _id === message._id)
          if (prevIndex !== -1) {
            copyMessages[prevIndex] = message
          }
          return copyMessages
        })
      })

      webinarSocket.on(SocketEvents.READY, (message) => {
        if (message && webinarData.activeNMOCodes) sendFirstWatching()
        resetMessages()
      })

      webinarSocket.on(SocketEvents.WEBINAR_INFO, (message: any) => {
        setWebinar((data: IMaterial) => ({ ...data, ...message }))
      })

      webinarSocket.on(SocketEvents.VOTE_STATE, (message: any) => {
        setSurvey(message)
      })

      webinarSocket.on(SocketEvents.SHOW_QUIZZ_RESULTS, (message: any) => {
        setSurvey(message)
      })
    }
  }, [webinarSocket])

  return (
    <WebinarSocketContext.Provider
      value={{
        webinar: webinarData,
        webinarStatus,
        chatMessages,
        checkup,
        viewers,
        rating,
        isFirst,
        sendConfirm,
        handleWebSocketSend,
        handleTopThrottled,
        subscribeAllLectors,
        sendSurveyVote,
        isBlocked,
        deleteSingleMessage,
        deleteAllUserMessages,
        sendRating,
        sendFirstWatching,
      }}
    >
      {children}
    </WebinarSocketContext.Provider>
  )
}

export default WebinarSocketWrap
